// Network layer for the snippet. Two surfaces:
//   1. fetchConfig(url, etag) → public config endpoint with conditional GET.
//   2. postConsent(url, body) → consent endpoint with keepalive + retry queue.

import type { Config, PendingEvent } from './types';
import { PENDING_KEY } from './storage';

interface ApiCategoryDef {
  id: 'strictly_necessary' | 'functional' | 'analytics' | 'marketing';
  label: string;
  description?: string;
  required?: boolean;
}

interface ApiConfigResponse {
  publicId: string;
  policyRevision: number;
  bannerTextHash: string;
  language: 'en' | 'ga';
  categories: ApiCategoryDef[];
  copy: Config['copy'];
  theme: Config['theme'];
  defaults: Config['defaults'];
  currentSnippetVersion: string;
}

const ETAG_KEY = 'cookiesie_cfg_etag';
const CFG_KEY = 'cookiesie_cfg';

export async function fetchConfig(url: string): Promise<Config | null> {
  let cachedEtag: string | null = null;
  let cachedConfig: Config | null = null;
  try {
    cachedEtag = window.localStorage.getItem(ETAG_KEY);
    const raw = window.localStorage.getItem(CFG_KEY);
    if (raw) cachedConfig = JSON.parse(raw) as Config;
  } catch {}

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: cachedEtag ? { 'if-none-match': cachedEtag } : undefined,
      credentials: 'omit',
      cache: 'default',
    });
  } catch {
    return cachedConfig;
  }

  if (response.status === 304 && cachedConfig) return cachedConfig;
  if (!response.ok) return cachedConfig;

  let parsed: ApiConfigResponse;
  try {
    parsed = (await response.json()) as ApiConfigResponse;
  } catch {
    return cachedConfig;
  }

  // Shape-validate the parsed body. `response.json()` only throws on
  // syntax errors — `null`, `{}`, error envelopes, and CDN status-pages
  // parsed as JSON all pass `try/catch` and would otherwise be written to
  // localStorage as a poisoned config that crashes banner.ts on every
  // subsequent load until the user clears storage.
  if (
    !parsed ||
    typeof parsed !== 'object' ||
    typeof parsed.policyRevision !== 'number' ||
    typeof parsed.bannerTextHash !== 'string' ||
    !parsed.copy ||
    !parsed.theme ||
    !parsed.defaults ||
    !Array.isArray(parsed.categories)
  ) {
    return cachedConfig;
  }

  const config: Config = {
    categories: parsed.categories,
    copy: parsed.copy,
    theme: parsed.theme,
    defaults: parsed.defaults,
    policyRevision: parsed.policyRevision,
    language: parsed.language,
    bannerTextHash: parsed.bannerTextHash,
  };

  try {
    const etag = response.headers.get('etag');
    if (etag) window.localStorage.setItem(ETAG_KEY, etag);
    window.localStorage.setItem(CFG_KEY, JSON.stringify(config));
  } catch {}

  return config;
}

export type PostConsentOutcome =
  | { kind: 'ok' }
  | { kind: 'retry' } // transient: 0 / 5xx / network — queue + try again later
  | { kind: 'drop'; status: number }; // terminal: 4xx that won't change on retry

export async function postConsent(url: string, evt: PendingEvent): Promise<PostConsentOutcome> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: { 'content-type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({
        projectId: evt.projectId,
        choice: evt.choice,
        categories: evt.categories,
        consentString: evt.consentString,
        tcfConsentString: null,
        uiVersion: evt.uiVersion,
        snippetVersion: evt.snippetVersion,
        language: evt.language,
        policyRevision: evt.policyRevision,
        bannerTextHash: evt.bannerTextHash,
        pageUrl: evt.pageUrl,
      }),
    });
  } catch {
    return { kind: 'retry' };
  }
  if (res.ok || res.status === 204) return { kind: 'ok' };
  // Terminal 4xxs that won't succeed on retry — drop instead of looping
  // the device's queue forever. 422 = banner_text_hash_mismatch (the
  // customer republished); 404 = project not found / archived; 403 = origin
  // not allowed. For 429 we inspect the body: rate_limited is transient,
  // quota_exceeded is terminal for the rest of the month.
  if (res.status === 422 || res.status === 404 || res.status === 403) {
    return { kind: 'drop', status: res.status };
  }
  if (res.status === 429) {
    try {
      const body = (await res.clone().json()) as { error?: { code?: string } };
      if (body?.error?.code === 'quota_exceeded') {
        return { kind: 'drop', status: 429 };
      }
    } catch {}
    return { kind: 'retry' };
  }
  // Any other 4xx is terminal-by-default; 5xx + 0 is transient.
  if (res.status >= 400 && res.status < 500) return { kind: 'drop', status: res.status };
  return { kind: 'retry' };
}

export function loadPendingQueue(): PendingEvent[] {
  try {
    const raw = window.localStorage.getItem(PENDING_KEY);
    if (!raw) return [];
    // Anything non-array (null from a future-schema write, primitive from a
    // privacy-extension rewrite, object from a corrupted slot) would crash
    // `queue.push()` in the synchronous pre-fetch path of sendConsent,
    // aborting applyConsent after the cookie is written but before the
    // banner is removed. Default to an empty queue on shape mismatch.
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as PendingEvent[]) : [];
  } catch {
    return [];
  }
}

export function savePendingQueue(arr: PendingEvent[]): void {
  try {
    while (arr.length > 5) arr.shift();
    window.localStorage.setItem(PENDING_KEY, JSON.stringify(arr));
  } catch {}
}

export function clearPendingQueue(): void {
  try {
    window.localStorage.removeItem(PENDING_KEY);
  } catch {}
}
